import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Avatar,
  Button,
  ConnectedLanguageSwitcher,
  RoleBadge,
  ThemeToggle,
  TopNav,
  type AdminRole,
} from '@bravemove/ui-web';
import type { AdminUser } from '@bravemove/types';
import { useAuth } from '../auth/useAuth';
import { PageHintButton } from '../components/PageHintButton';
import { resolvePageHintKey } from '../lib/pageHints';
import { usePageTitleContext } from './PageTitleContext';

export interface AdminTopNavProps {
  readonly adminUser: AdminUser;
}

/**
 * F06 — top bar: brand · active page title + ⓘ hint · theme toggle · language
 * switcher · role-tagged user menu. No search ships at MVP; the admin in-app
 * notification bell was removed by design (see the inline note in `end` —
 * the F26 inbox FKs users.id, so admins are alerted via email + the sidebar
 * pending-count badges).
 *
 * The user menu is a lightweight click-toggle popover (no Popover component
 * needed for 2 links — keep deps minimal).
 */
export function AdminTopNav({ adminUser }: AdminTopNavProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // The active page's title (lifted from PageHeader) + its ⓘ hint render in the
  // top bar's centre slot, replacing the per-page body title.
  const location = useLocation();
  const titleCtx = usePageTitleContext();
  const hintKey = resolvePageHintKey(location.pathname);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <TopNav
      center={
        titleCtx?.title ? (
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-ink">
              {titleCtx.title}
            </h1>
            {hintKey ? <PageHintButton text={t(hintKey)} label={t('admin:hints.aria')} /> : null}
          </div>
        ) : null
      }
      end={
        <div className="flex items-center gap-2">
          {/* Admin in-app notification bell removed: the inbox is consumer/B2B-only
              (in_app_notifications.recipient_user_id FKs users.id, not admins).
              Admins are alerted by email + the sidebar pending-count badges. A
              proper admin-recipient inbox is a post-MVP F26 follow-up. */}
          <ThemeToggle />
          <ConnectedLanguageSwitcher availableLanguages={['en']} />
          <div ref={menuRef} className="relative">
            <button
              type="button"
              aria-label={t('admin:topNav.userMenu')}
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center gap-2 ps-2 pe-3 h-9 rounded-md hover:bg-surface-2 transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25"
            >
              <Avatar name={adminUser.displayName ?? adminUser.email} size="xs" />
              <span className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-[12.5px] font-semibold text-ink truncate max-w-[160px]">
                  {adminUser.displayName ?? adminUser.email}
                </span>
                <span className="text-[10.5px] text-ink-3 font-mono uppercase tracking-wider">
                  {t(`admin:roles.${adminUser.role}`)}
                </span>
              </span>
              <RoleBadge
                role={adminUser.role as AdminRole}
                size="sm"
                className="hidden lg:inline-flex"
              />
            </button>
            {menuOpen ? (
              <div
                role="menu"
                className="absolute end-0 mt-2 min-w-[220px] rounded-xl bg-surface border border-hairline shadow-[0_16px_40px_-20px_rgba(0,0,0,0.20)] py-1 z-40"
              >
                <div className="px-3 py-2 border-b border-hairline">
                  <div className="text-[12.5px] font-semibold text-ink truncate">
                    {adminUser.displayName ?? adminUser.email}
                  </div>
                  <RoleBadge role={adminUser.role as AdminRole} size="sm" className="mt-1.5" />
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/account');
                  }}
                  className="w-full text-start px-3 py-2 text-[13px] text-ink hover:bg-surface-2"
                >
                  {t('admin:nav.account')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    void auth.logout().then(() => navigate('/login'));
                  }}
                  className="w-full text-start px-3 py-2 text-[13px] text-ink hover:bg-surface-2"
                >
                  {t('admin:nav.logout')}
                </button>
              </div>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void auth.logout().then(() => navigate('/login'))}
            className="hidden md:inline-flex"
          >
            {t('admin:nav.logout')}
          </Button>
        </div>
      }
    />
  );
}
